import axios, { AxiosError, type AxiosInstance } from 'axios'
import type { ApiEnvelope } from '@/types/api'

export interface ApiError {
  code: string
  message: string
  statusCode: number
  details?: Record<string, unknown>
}

/**
 * The single axios instance. Nothing else in the app creates one — every
 * request must carry the session cookie and go through the same error shaping.
 *
 * The default timeout covers ordinary reads and writes. It deliberately does
 * NOT cover a long LLM-backed request, which can take minutes: a client-side
 * abort then shows the user a failure for work the server is still completing
 * successfully. Such calls pass `LONG_REQUEST_TIMEOUT_MS` explicitly.
 */
export const http: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 120_000,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Ceiling for a long LLM-backed request. Long enough that slow work finishes
 * rather than being cut off, short enough that a genuinely hung request
 * eventually reports instead of hanging forever. Matches the dev proxy
 * (vite.config.ts) and nginx (nginx.conf) timeouts.
 */
export const LONG_REQUEST_TIMEOUT_MS = 15 * 60 * 1000

/** Unwraps the { data, message, statusCode } envelope the backend always sends. */
export const unwrap = <T>(payload: ApiEnvelope<T>): T => payload.data

const SESSION_ERROR_CODES = new Set(['SESSION_EXPIRED', 'UNAUTHENTICATED', 'ACCOUNT_INACTIVE'])

/** Set by the router so the interceptor can redirect without importing it. */
let onSessionLost: (() => void) | null = null
export const setSessionLostHandler = (handler: () => void): void => {
  onSessionLost = handler
}

/**
 * The error shape every caller sees, from the API's error envelope (or none,
 * when the server could not be reached). A lost session is not a page-level
 * error; it is a redirect. The login request itself is exempt, or a wrong
 * email would bounce the user. Shared by `http` and `streamEvents`.
 */
export const toApiError = (
  payload: Partial<ApiError> | undefined,
  statusCode: number,
  url: string | undefined,
): ApiError => {
  const apiError: ApiError = {
    code: payload?.code ?? 'NETWORK_ERROR',
    message: payload?.message ?? 'Could not reach the server. Please try again.',
    statusCode,
    details: payload?.details,
  }
  const isLoginRequest = url?.includes('/auth/login') === true
  if (SESSION_ERROR_CODES.has(apiError.code) && !isLoginRequest) onSessionLost?.()
  return apiError
}

http.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) =>
    Promise.reject(toApiError(error.response?.data, error.response?.status ?? 0, error.config?.url)),
)

export const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && error !== null && 'code' in error && 'message' in error
